import React, { useState } from 'react';
import { NavLink, Outlet, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BrandLogo from './BrandLogo';

function CalendarNavIcon() {
  return (
    <svg
      className="nav-svg-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4.5" width="18" height="16" rx="2" />
      <line x1="3" y1="9.5" x2="21" y2="9.5" />
      <line x1="8" y1="2.5" x2="8" y2="6.5" />
      <line x1="16" y1="2.5" x2="16" y2="6.5" />
    </svg>
  );
}

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', icon: '📊' },
  { path: '/customers', label: 'Customers', icon: '👥' },
  { path: '/templates', label: 'Templates', icon: '✉️' },
  { path: '/subscription', label: 'Subscription', icon: CalendarNavIcon },
  { path: '/my-tickets', label: 'Support', icon: '💬' },
  { path: '/settings', label: 'Settings', icon: '⚙️' },
];

export default function Layout() {
  const { user, hasBusinessSetup, businessName, logout, isDemo } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const displayName = isDemo ? 'Glow Salon' : (businessName || user?.email);

  const handleSignUp = () => {
    logout();
    navigate('/signup');
  };

  if (!hasBusinessSetup) {
    return <Navigate to="/setup" />;
  }

  return (
    <div className="app-layout">
      <div className="mobile-header">
        <button className="hamburger" onClick={() => setSidebarOpen(true)}>
          ☰
        </button>
        <BrandLogo className="sidebar-logo" />
        <div style={{ width: 40 }} />
      </div>

      <div className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <BrandLogo className="sidebar-logo" />
        <nav className="sidebar-nav">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              <span className="icon">
                {typeof item.icon === 'string' ? item.icon : <item.icon />}
              </span>
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          {isDemo && (
            <div style={{
              background: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)',
              border: '1px solid #C4B5FD',
              borderRadius: 10,
              padding: '10px 12px',
              marginBottom: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  background: '#7C3AED',
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  padding: '2px 8px',
                  borderRadius: 20,
                  textTransform: 'uppercase',
                }}>
                  ✦ Demo Account
                </span>
              </div>
              <p style={{ fontSize: 11, color: '#5B21B6', margin: '0 0 8px', lineHeight: 1.4 }}>
                You're exploring a demo. Sign up free to save your data.
              </p>
              <button
                onClick={handleSignUp}
                style={{
                  width: '100%',
                  background: '#7C3AED',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 7,
                  padding: '6px 0',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  letterSpacing: '0.01em',
                }}
              >
                Sign Up Free →
              </button>
            </div>
          )}
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8, fontWeight: businessName || isDemo ? 600 : 400 }}>
            {displayName}
          </div>
          <div style={{ fontSize: 12, marginBottom: 12 }}>
            {user?.plan === 'free' ? (
              <span className="badge badge-warning" style={{ textTransform: 'capitalize' }}>
                No plan — <NavLink to="/subscription" style={{ color: 'inherit', textDecoration: 'underline' }}>upgrade</NavLink>
              </span>
            ) : (
              <span className="badge badge-purple" style={{ textTransform: 'capitalize' }}>
                {user?.plan} plan
              </span>
            )}
          </div>
          <button className="btn btn-secondary btn-sm btn-block" onClick={logout}>
            {isDemo ? 'Exit Demo' : 'Log out'}
          </button>
        </div>
      </aside>

      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}
